import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { RotateCcw } from 'lucide-react';
import { useTestAccess, TestAccess } from '../hooks/use-test';
import { TEST_BLOCKED_REASON } from '../constants/test-blocked-reason';
import { TEST_PERIOD_NOUN } from '../constants/test-period';
import { Sentry } from '../lib/sentry';

interface TestCardProps {
  organisationId: number;
}

function describeTestsDone(access: TestAccess): string {
  const periodNoun = TEST_PERIOD_NOUN[access.period];
  return `${access.testsCompleted} of ${access.maxTests} tests done this ${periodNoun}`;
}

export function TestCard({ organisationId }: TestCardProps) {
  const navigate = useNavigate();
  const { data: access, error } = useTestAccess(organisationId);

  useEffect(() => {
    if (error) Sentry.captureException(error);
  }, [error]);

  if (!access?.canAccess) {
    return null;
  }

  const isMaxReached =
    access.limitsEnabled && access.reason === TEST_BLOCKED_REASON.MAX_REACHED;

  return (
    <div className="mb-6">
      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        <div className="p-5">
          <div className="flex items-center mb-6">
            <div className="w-14 h-14 rounded-full bg-accent-purple/10 overflow-hidden flex items-center justify-center mr-4">
              <RotateCcw className="w-7 h-7 text-accent-purple" />
            </div>
            <div>
              <h3 className="font-bold text-title text-lg">Test Mode</h3>
              <p className="text-sm text-subheading">
                Test your knowledge with questions from your courses
              </p>
            </div>
          </div>

          {isMaxReached && (
            <p className="text-sm text-subheading mb-2">
              {describeTestsDone(access)}
            </p>
          )}

          <Button
            onClick={() => navigate('/test')}
            disabled={!access.canStart}
            className="w-full rounded-full bg-accent-purple hover:bg-accent-purple/90 text-white py-6 h-12 mt-2"
          >
            Start Test
          </Button>
        </div>
      </div>
    </div>
  );
}
